import type { getSecureNotesApi } from '@frontend/api-client';
import type { Client, LocalFile, ReplaceSecureNoteInput, TypedFormData } from '@frontend/api-types';
import type { SnakeCasedProperties } from '@frontend/duck-tape';
import type { SecureNoteFormSchema } from '@frontend/forms';
import type { APIMutation, GetMutationUtilArgs } from '@frontend/react-domain/utils/mutations';

export type CreateSecureNoteArgs = GetMutationUtilArgs<{ secureNoteId: string }> & {
  client: MaybeUndefined<Client>;
  createSecureNoteMutation: APIMutation<typeof getSecureNotesApi, 'useCreateSecureNoteMutation'>;
  data: SecureNoteFormSchema;
  formDataClass: typeof TypedFormData;
  updateSecureNoteMutation: APIMutation<typeof getSecureNotesApi, 'useReplaceSecureNoteContentMutation'>;
};

export const createSecureNote = async ({
  client,
  createSecureNoteMutation,
  data,
  formDataClass,
  handleMutation,
  handleMutationOptions,
  updateSecureNoteMutation,
}: CreateSecureNoteArgs) => {
  if (!client?.id) return;
  const { attachments, notes, password, title, username, website } = data;
  return handleMutation({
    mutation: async () => {
      const secureNote = await createSecureNoteMutation({ client: client.id, title }).unwrap();
      if (secureNote.id) {
        const formData = new formDataClass<SnakeCasedProperties<ReplaceSecureNoteInput>>();

        if (attachments.length) formData.appendLocalFiles('attachments', attachments as LocalFile[]);
        if (notes) formData.append('notes', notes);
        if (password) formData.append('password', password);
        if (username) formData.append('username', username);
        if (website) formData.append('website', website);

        await updateSecureNoteMutation({ formData, secureNoteId: secureNote.id }).unwrap();
      }
      return { secureNoteId: secureNote.id };
    },
    successMessage: 'Successfully created.',
    ...handleMutationOptions,
  });
};
